import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Odds from "@/components/Odds";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  LayoutGrid, Crown, FileSearch, ListChecks, Wallet, Users, Settings,
  Sparkles, ArrowRight, Lock, ShieldCheck,
} from "lucide-react";
import { fetchDashboardSummary, fetchChart, fetchConfig, MARKET_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PickCard from "@/components/PickCard";
import StatusBadge from "@/components/StatusBadge";
import Stars from "@/components/Stars";
import { Skeleton } from "@/components/ui/skeleton";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { VipPicksTab, AnalysisTab, ResultsTab, BankrollTab, CommunityTab, AccountTab } from "@/components/dashboard/DashboardTabs";

const SKELETON_KEYS = ["a", "b", "c", "d"];

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutGrid },
  { key: "vip-picks", label: "VIP Picks", icon: Crown },
  { key: "analysis", label: "Analysis", icon: FileSearch },
  { key: "results", label: "Results", icon: ListChecks },
  { key: "bankroll", label: "Bankroll", icon: Wallet },
  { key: "community", label: "Community", icon: Users },
  { key: "account", label: "Account", icon: Settings },
];

const TAB_VIEWS = {
  overview: ({ data, isVip, onUnlock }) => <Overview data={data} isVip={isVip} onUnlock={onUnlock} />,
  "vip-picks": VipPicksTab,
  analysis: AnalysisTab,
  results: ResultsTab,
  bankroll: BankrollTab,
  community: CommunityTab,
  account: AccountTab,
};

function UnitsChart() {
  const { data } = useQuery({ queryKey: ["chart"], queryFn: fetchChart });
  return (
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data?.points || []} margin={{ top: 5, right: 8, left: -22, bottom: 0 }}>
          <defs>
            <linearGradient id="dashUnits" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#00E676" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#00E676" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(d) => dayjs(d).format("MMM D")} axisLine={false} tickLine={false} minTickGap={40} />
          <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: "#0F131D", border: "1px solid #2A364F", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 12 }} labelStyle={{ color: "#94A3B8" }} />
          <Area type="monotone" dataKey="units" stroke="#00E676" strokeWidth={2} fill="url(#dashUnits)" dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function Overview({ data, isVip, onUnlock }) {
  const stats = data?.stats;
  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      {stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { label: "Season Record", value: stats.record },
            { label: "Net Units", value: `${stats.net_units > 0 ? "+" : ""}${stats.net_units}U` },
            { label: "ROI", value: `${stats.roi > 0 ? "+" : ""}${stats.roi}%` },
            { label: "Last 10", value: stats.last10 },
          ].map((s) => (
            <div key={s.label} className="card-mp p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{s.label}</p>
              <p className="mt-1.5 font-mono text-xl font-bold text-[#00E676] sm:text-2xl">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {data?.best_bet && (
        <div className="card-mp border-[#00E676]/40 p-5 sm:p-6" data-testid="dashboard-best-bet">
          <span className="tag border border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676]">
            <Sparkles size={10} /> Tonight's Best Bet
          </span>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-wider text-slate-500">{data.best_bet.game}</p>
              <h3 className="mt-1 font-display text-2xl font-bold uppercase text-white">
                {data.best_bet.player} <span className="text-[#00E676]">{data.best_bet.selection}</span>
              </h3>
              <div className="mt-2 flex items-center gap-3 font-mono text-xs text-slate-400">
                <span>Odds <b className="text-white"><Odds value={data.best_bet.odds} /></b></span>
                <span>Stake <b className="text-white">{data.best_bet.units}U</b></span>
                <Stars value={data.best_bet.confidence} />
              </div>
            </div>
            <Link to="/picks" data-testid="dashboard-best-bet-cta" className="btn-outline !px-4 !py-2">
              View Card <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card-mp p-5 sm:p-6" data-testid="dashboard-slate">
          <h3 className="font-display text-lg font-bold uppercase tracking-wide text-white">Tonight's NBA Slate</h3>
          <div className="mt-4 space-y-3">
            {(data?.tonight_slate || []).map((g) => (
              <div key={g.id} className="flex items-center justify-between rounded-lg border border-[#1E2738] bg-[#0F131D] px-4 py-3">
                <span className="text-sm font-semibold text-slate-200">{g.away_abbr} @ {g.home_abbr}</span>
                <div className="flex items-center gap-3 font-mono text-xs text-slate-400">
                  <span>{g.spread}</span>
                  <span className="text-slate-500">{g.time_label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-mp p-5 sm:p-6" data-testid="dashboard-recent-results">
          <h3 className="font-display text-lg font-bold uppercase tracking-wide text-white">Recent Results</h3>
          <div className="mt-4 space-y-3">
            {(data?.recent_results || []).map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-lg border border-[#1E2738] bg-[#0F131D] px-4 py-3">
                <span className="truncate text-sm font-semibold text-slate-200">
                  {p.player ? `${p.player} ${p.selection}` : p.selection}
                </span>
                <StatusBadge status={p.status} profit={p.profit} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card-mp p-5 sm:p-6" data-testid="dashboard-units-chart">
        <h3 className="mb-3 font-display text-lg font-bold uppercase tracking-wide text-white">Season Units</h3>
        <UnitsChart />
      </div>

      <div data-testid="dashboard-vip-picks-preview">
        <h3 className="mb-4 font-display text-lg font-bold uppercase tracking-wide text-white">Latest VIP Picks</h3>
        <div className="grid gap-6 md:grid-cols-2">
          {(data?.latest_vip_picks || []).map((p, i) => (
            <PickCard key={p.id} pick={p} index={i} onUnlock={onUnlock} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, isVip, isOwner, logout, checking, discordInviteUrl, membershipStatus, membershipPlan, membershipRenewsAt, membershipCancelAtPeriodEnd, vipTestMode } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const { data: config } = useQuery({ queryKey: ["config"], queryFn: fetchConfig, staleTime: 300_000 });
  const { data, isLoading } = useQuery({ queryKey: ["dashboard", isVip], queryFn: fetchDashboardSummary, enabled: !!user });
  const vipUrl = config?.whop?.vip_checkout_url || "/#pricing";

  if (checking) {
    return <div className="mx-auto max-w-7xl px-4 py-24 text-center font-mono text-xs text-slate-500" data-testid="dashboard-checking">Checking membership...</div>;
  }

  if (!user) {
    return (
      <div className="mx-auto flex max-w-7xl items-center justify-center px-4 py-24 sm:px-6 lg:px-8" data-testid="dashboard-login-gate">
        <div className="panel max-w-md p-8 text-center">
          <Lock size={28} className="mx-auto text-[#F59E0B]" />
          <h1 className="mt-4 font-display text-2xl font-extrabold uppercase text-white">Members Only</h1>
          <p className="mt-2 text-sm text-slate-400">Log in to access the Money Printer member dashboard.</p>
          <button onClick={() => navigate("/login")} data-testid="dashboard-goto-login-btn" className="btn-primary mt-6 w-full">
            Go To Login
          </button>
        </div>
      </div>
    );
  }

  const onUnlock = () => {
    if (vipUrl.startsWith("http")) window.open(vipUrl, "_blank", "noopener");
    else navigate("/#pricing");
  };

  const tabProps = { data, isVip, onUnlock, user, vipUrl, discordUrl: discordInviteUrl, chart: <UnitsChart />, onLogout: async () => { await logout(); navigate("/"); } };
  const TabView = TAB_VIEWS[tab];
  const renderTab = () => {
    if (isLoading) {
      return (
        <div className="grid gap-6 md:grid-cols-2">
          {SKELETON_KEYS.map((k) => <Skeleton key={k} className="h-40 rounded-xl bg-[#141A26]" />)}
        </div>
      );
    }
    return TabView ? <TabView {...tabProps} /> : null;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="dashboard-page">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow">Member Dashboard</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
            Welcome back, <span className="text-[#00E676]">{user.name.split(" ")[0]}</span>
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isOwner && (
            <Link to="/admin" data-testid="dashboard-admin-console-link" className="btn-primary !px-5 !py-2.5">
              <ShieldCheck size={15} /> Admin Console
            </Link>
          )}
          <span data-testid="dashboard-plan-badge" className={`tag border ${isVip ? "border-[#F59E0B]/50 bg-[#F59E0B]/10 text-[#F59E0B]" : "border-[#00E676]/50 bg-[#00E676]/10 text-[#00E676]"}`}>
            {isOwner ? "Owner" : isVip ? `VIP Member${membershipPlan ? ` · ${membershipPlan}` : ""}` : "Free Member"}{membershipStatus && !isOwner ? ` · ${membershipStatus}` : ""}{membershipCancelAtPeriodEnd && membershipRenewsAt ? ` · access until ${new Date(membershipRenewsAt).toLocaleDateString()}` : ""}{vipTestMode === "vip" ? " · Test mode" : ""}
          </span>
        </div>
      </div>

      {!isVip && (
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-5" data-testid="dashboard-upgrade-banner">
          <div>
            <p className="font-display text-base font-bold uppercase tracking-wide text-[#F59E0B]">No active VIP membership found</p>
            <p className="mt-1 text-sm text-slate-300">Join VIP on Whop with this account and your full analysis, VIP picks and Discord access unlock automatically.</p>
          </div>
          <a href={vipUrl} target={vipUrl.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid="dashboard-upgrade-btn" className="btn-primary !px-5 !py-2.5"><Crown size={15} /> Join VIP</a>
        </div>
      )}

      <div className="flex flex-col gap-8 lg:flex-row">
        <nav className="lg:w-56 lg:shrink-0" aria-label="Dashboard" data-testid="dashboard-nav">
          <div className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0">
            {NAV.map((n) => (
              <button
                key={n.key}
                data-testid={`dashboard-tab-${n.key}`}
                onClick={() => setTab(n.key)}
                className={`flex shrink-0 items-center gap-2.5 rounded-lg border px-4 py-2.5 font-display text-sm font-bold uppercase tracking-wider transition-colors lg:w-full ${
                  tab === n.key
                    ? "border-[#00E676] bg-[#00E676]/10 text-[#00E676]"
                    : "border-[#1E2738] text-slate-400 hover:border-[#2A364F] hover:text-white"
                }`}
              >
                <n.icon size={15} /> {n.label}
              </button>
            ))}
          </div>
        </nav>
        <div className="min-w-0 flex-1">{renderTab()}</div>
      </div>
    </div>
  );
}
